import { formItemLayout } from '../../../common';
import {
  FieldMappings,
  InputFileUploadResult,
  PreviousFieldMappings,
} from '../../../common/components/frontend-processing/models';
import { toInputLayoutFields } from '../../../common/components/frontend-processing/utils';
import { FormLayout, InputFileInformationModel } from '../common';
import { ErrorsAndWarningReporter } from '../../../common/components/frontend-processing/ErrorsAndWarningReporter';
import { InputFileProcessor } from '../../../common/components/frontend-processing/input-field/InputFileProcessor';
import { ProgramWizardStepDataProcessor } from '../common/ProgramWizardStepDataProcessor';
import { generateId } from '@/app/core/utils';
import {
  useDataPart,
  useDataStore,
  useStepContext,
} from '@/app/core/components/step-wizard';
import {
  InputFileProps,
  ModuleInputProps,
} from '@/app/features/products/common/components/input-props';
import {
  InputFileDetails,
  InputFileSelector,
} from '@/app/features/products/common/components/file-selection';
import { ElementWithFeedbackContainer } from '@/app/core/components/ElementWithFeedbackContainer';
import { Table } from 'antd';
import { useState, useEffect, useCallback } from 'react';
import { yupResolver } from '@hookform/resolvers';
import { Controller, useForm } from 'react-hook-form';
import {
  asFieldsWithDimensionsContainer,
  HasFieldDimensions,
  InputLayoutField,
  FieldDelimiter,
} from '@ssar-app/domain';
import { Alert, FormLabel, Modal, Spin } from '@ssar-app/uikit';
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  Grid,
  GridItem,
  HStack,
  Input,
  SimpleGrid,
  Text,
} from '@chakra-ui/react';
import { ColumnsType } from 'antd/lib/table';
import { ObjectSchema } from 'yup';
import { FaInfoCircle } from 'react-icons/fa/index.js';

type CustomInputFileInformationModel = InputFileInformationModel & {
  fieldsInfo: {
    fieldsErrors?: {
      addressMinimums?: string;
      nameMinimums?: string;
      addressCombinations?: string;
      nameCombinations?: string;
    };
  };
};

interface ErrorComponentProps {
  localError?: string;
  text: string;
}

export const InputFileInformationSelection = (
  props: ModuleInputProps<InputFileProps>,
) => {
  const { stepKey, validator } = props;
  const {
    productName,
    inputFileSelectionOptions,
    inputLabelsMap,
    isLoading: showLoader,
  } = props.moduleProps;

  const { data } = useDataStore();
  const { isStepActive } = useStepContext();
  const [dataPartState] = useDataPart<InputFileInformationModel>(stepKey);
  const [inputFileInformationModel, setInputFileInformationModel] = useState<
    Partial<InputFileInformationModel>
  >(dataPartState.data || {});

  const formMethods = useForm<CustomInputFileInformationModel>({
    defaultValues: inputFileInformationModel,
    mode: 'onChange',
    context: {},
    resolver: yupResolver(validator, {
      abortEarly: false,
    }),
  });
  const { errors, control, formState, setValue, watch, trigger } = formMethods;

  const [isLoading, setIsLoading] = useState(false);
  const [showMlNotification, setShowMlNotification] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [inputFileDetails, setInputFileDetails] = useState<InputFileDetails>();
  const [showFileProcessorModal, setShowFileProcessorModal] = useState(false);
  const [isRemoteFile, setIsRemoteFile] = useState(true);
  const [selectedFieldMappings, setSelectedFieldMappings] = useState<
    FieldMappings[]
  >(() => {
    const toReturn = (
      inputFileInformationModel?.fieldsInfo?.fields || []
    ).reduce((acc, curr, index) => {
      const recordIndex = acc.findIndex(
        (a) =>
          a.field ===
          (curr.headerName === '' ? index.toString() : curr.headerName || ''),
      );
      const toInsert: FieldMappings = {
        field:
          curr.headerName === '' ? index.toString() : curr.headerName || '',
        headerNameWithLength: {
          name: curr.name,
          fieldLength: curr.fieldLength,
        },
      };
      if (recordIndex === -1) {
        acc.push(toInsert);
      } else {
        acc[recordIndex] = toInsert;
      }
      return acc;
    }, [] as FieldMappings[]);
    return toReturn;
  });

  const previousFieldMappings = selectedFieldMappings.reduce((acc, curr) => {
    const recordIndex = acc.findIndex((a) => a.field === curr.field);
    const toInsert: PreviousFieldMappings = {
      field: curr.field,
      previousHeader: curr.headerNameWithLength?.name,
    };
    if (recordIndex === -1) {
      acc.push(toInsert);
    } else {
      acc[recordIndex] = toInsert;
    }
    return acc;
  }, [] as PreviousFieldMappings[]);

  const mappedFields: HasFieldDimensions<InputLayoutField>[] =
    asFieldsWithDimensionsContainer(watch('fieldsInfo.fields', [])).fields;
  const isSSNFieldFound = mappedFields
    .map((a) => (a.name || a.headerName || '').toLowerCase())
    .includes('ssn');

  const minimumsCombinationsErrors: string[] = [
    errors?.fieldsInfo?.fieldsErrors?.addressCombinations?.type?.toString() ||
      null,
    errors?.fieldsInfo?.fieldsErrors?.nameCombinations?.type.toString() || null,
    errors?.fieldsInfo?.fieldsErrors?.addressMinimums?.type.toString() || null,
    errors?.fieldsInfo?.fieldsErrors?.nameMinimums?.type.toString() || null,
  ]
    .map((a) => (a || '').toString())
    .filter((a) => a.length > 0);

  const onFileUploadSuccess = useCallback(
    (uploadResult: InputFileUploadResult): void => {
      setIsLoading(false);
      setInputFileDetails(undefined);
      setSelectedFieldMappings(uploadResult.fieldMappings);
      setValue('clientFileName', uploadResult.clientFileName, {
        shouldDirty: true,
        shouldValidate: true,
      });
      setValue('fileName', uploadResult.experianFileName, {
        shouldDirty: true,
        shouldValidate: true,
      });
      setValue(
        'numberOfRecordsExpected',
        uploadResult.recordsExpected ||
          inputFileInformationModel.numberOfRecordsExpected,
        {
          shouldDirty: true,
          shouldValidate: true,
        },
      );

      const inputLayoutFields = toInputLayoutFields(
        uploadResult.fieldMappings,
        !!uploadResult.hasHeaders,
      );
      formMethods.clearErrors('fieldsInfo.fieldsErrors');
      setValue('fieldsInfo.fields', inputLayoutFields, {
        shouldDirty: true,
        shouldValidate: true,
      });

      const newInfo = {
        ...inputFileInformationModel,
        numberOfRecordsExpected:
          uploadResult?.recordsExpected ||
          inputFileInformationModel.numberOfRecordsExpected,
        clientFileName: uploadResult.clientFileName,
        fileName: uploadResult.experianFileName,
        hasHeaders: uploadResult.hasHeaders,
        isRemoteUploadedFile: isRemoteFile,
        fieldsInfo: {
          fields: inputLayoutFields,
          source: inputFileInformationModel.fieldsInfo?.source || 'Inline',
        },
        delimiter: inputFileInformationModel.delimiter || FieldDelimiter.COMMA,
      };
      setInputFileInformationModel(newInfo);
      setIsModalVisible(false);
    },
    [formMethods, setValue, inputFileInformationModel, isRemoteFile],
  );

  useEffect(() => {
    if (inputFileInformationModel) {
      setValue(
        'fieldsInfo.fields',
        inputFileInformationModel.fieldsInfo?.fields,
        {
          shouldDirty: true,
          shouldValidate: true,
        },
      );
    }
  }, [inputFileInformationModel, setValue]);

  const ErrorComponent = (props: ErrorComponentProps) => {
    const { localError, text } = props;
    return localError ? (
      <ElementWithFeedbackContainer errors={[localError]}>
        <Alert
          type='error'
          description={text}
          backgroundColor={'#e87a655e'}
          border={'1px solid #d62b1a'}
        ></Alert>
      </ElementWithFeedbackContainer>
    ) : (
      <>{text}</>
    );
  };

  const columns: ColumnsType<HasFieldDimensions<InputLayoutField>> = [
    {
      dataIndex: 'headerName',
      title: 'Client Header',
      render: (text, _row, index) => {
        const localError =
          formState.errors.fieldsInfo?.fields?.[index]?.headerName?.message;
        return <ErrorComponent localError={localError} text={text} />;
      },
    },
    {
      dataIndex: 'name',
      title: 'Experian Header',
      render: (text, row, index) => {
        const localError =
          formState.errors.fieldsInfo?.fields?.[index]?.name?.message;
        return <ErrorComponent localError={localError} text={text} />;
      },
    },
    {
      dataIndex: 'fieldLength',
      title: 'Length',
      align: 'center',
    },
    {
      dataIndex: 'begin',
      title: 'Begin',
      align: 'center',
    },
    {
      dataIndex: 'end',
      title: 'End',
      align: 'center',
    },
  ];

  return (
    <>
      <ProgramWizardStepDataProcessor
        stepKey={stepKey}
        trigger={trigger}
        triggerFieldNames={['numberOfRecordsExpected', 'clientFileName']}
        validator={validator as ObjectSchema}
        model={inputFileInformationModel}
        isDirty={formState.isDirty}
      />
      {isStepActive && (
        <Spin
          spinning={isLoading || showLoader}
          tip='Loading input file information...'
        >
          <Flex direction='column' w='full'>
            <form {...formItemLayout}>
              <FormLayout header={'File Details'}>
                <Controller
                  control={control}
                  name='experianInputFileName'
                  defaultValue={inputFileInformationModel.fileName || null}
                />
                <SimpleGrid columns={1} width={'full'} gap={4}>
                  <Alert
                    isOpen={data?.outputLayout?.isCustomized}
                    type='warning'
                    variant='left-accent'
                    background={'white'}
                    description={
                      <HStack color={'orange.700'}>
                        <FaInfoCircle />
                        <Text>
                          You have selected a Custom output layout for this job.
                          If you delete your input file or upload a new input
                          file, the output layout will be reset to the Standard
                          output layout.
                        </Text>
                      </HStack>
                    }
                    showCloseButton={false}
                    showIcon={false}
                  />
                  <Box>
                    <FormControl
                      isInvalid={errors.numberOfRecordsExpected ? true : false}
                      isRequired={true}
                    >
                      <Grid templateColumns='repeat(5,1fr)'>
                        <GridItem justifyContent='end' display='flex'>
                          <FormLabel marginTop={2}>Records Expected:</FormLabel>
                        </GridItem>
                        <GridItem>
                          <Controller
                            name='numberOfRecordsExpected'
                            control={control}
                            defaultValue={
                              inputFileInformationModel.numberOfRecordsExpected ||
                              null
                            }
                            render={({ value, onChange }) => (
                              <Input
                                type='number'
                                onChange={(e) => {
                                  onChange(e.target.value);
                                  const newRecords = Number(e.target.value);
                                  setInputFileInformationModel({
                                    ...inputFileInformationModel,
                                    numberOfRecordsExpected: newRecords,
                                  });
                                }}
                                defaultValue={value || null}
                                value={value}
                                spellCheck={false}
                              />
                            )}
                          ></Controller>
                          <FormErrorMessage>
                            {errors.numberOfRecordsExpected?.message}
                          </FormErrorMessage>
                        </GridItem>
                      </Grid>
                    </FormControl>
                  </Box>
                  <Box mt={6}>
                    <FormControl
                      isInvalid={errors.clientFileName ? true : false}
                    >
                      <Grid templateColumns='repeat(5,1fr)'>
                        <GridItem justifyContent='end' display='flex'>
                          <FormLabel>Client File Name :</FormLabel>
                        </GridItem>
                        <GridItem colSpan={4}>
                          <Controller
                            name='clientFileName'
                            control={control}
                            defaultValue={
                              inputFileInformationModel?.clientFileName || ''
                            }
                            render={({ value }) => (
                              <Flex alignItems='center'>
                                <Text fontSize='md' mr={value ? 8 : 0}>
                                  {value}
                                </Text>
                                <InputFileSelector
                                  onFileClicked={() => {
                                    setIsLoading(true);
                                  }}
                                  onCancel={() => {
                                    setIsLoading(false);
                                  }}
                                  subCode={data?.subCode}
                                  setIsRemoteFile={setIsRemoteFile}
                                  isCompact={
                                    !!inputFileInformationModel.clientFileName
                                  }
                                  onFileSelected={(details) => {
                                    setShowMlNotification(true);
                                    setInputFileDetails(details);
                                    setShowFileProcessorModal(true);
                                    setValue(
                                      'delimiter',
                                      details.fieldDelimiter,
                                      {
                                        shouldDirty: true,
                                        shouldValidate: true,
                                      },
                                    );
                                    setValue(
                                      'detectedHeaders',
                                      details.headersInfo.detectedHeaders,
                                      {
                                        shouldDirty: true,
                                        shouldValidate: true,
                                      },
                                    );
                                    setInputFileInformationModel({
                                      ...inputFileInformationModel,
                                      delimiter: details.fieldDelimiter,
                                      detectedHeaders:
                                        details.headersInfo.detectedHeaders,
                                    });
                                    setIsLoading(true);
                                    // setValue('delimiter', details.);
                                  }}
                                  onSuccess={(uploadResult) => {
                                    onFileUploadSuccess(uploadResult);
                                  }}
                                  inputLabelsMap={inputLabelsMap}
                                  inputFileSelectionOptions={
                                    inputFileSelectionOptions
                                  }
                                  productName={productName}
                                  oneClickRemoteFileAccess={true}
                                  isModalVisible={isModalVisible}
                                  setIsModalVisible={setIsModalVisible}
                                />
                              </Flex>
                            )}
                          ></Controller>
                          <FormErrorMessage>
                            {errors.clientFileName?.message
                              ? `${errors.clientFileName?.message}. Please select the input file to generate the file name.`
                              : null}
                          </FormErrorMessage>
                        </GridItem>
                      </Grid>
                    </FormControl>
                  </Box>
                  <Box>
                    <Controller
                      control={control}
                      name='inputLayout'
                      defaultValue={mappedFields || []}
                    />
                    <Flex mt={6}>
                      {mappedFields.length > 0 && (
                        <Grid templateColumns='repeat(5,1fr)'>
                          <GridItem justifyContent='end' display='flex'>
                            <FormLabel>File Layout:</FormLabel>
                          </GridItem>
                          <GridItem
                            colSpan={4}
                            display='flex'
                            flexDirection='row'
                          >
                            <Grid templateColumns='repeat(6,1fr)' gap={4}>
                              <GridItem colSpan={3}>
                                <Table
                                  bordered
                                  dataSource={mappedFields}
                                  columns={columns}
                                  pagination={false}
                                  size='small'
                                  // className="auto-width-table"
                                  rowKey={() => generateId()}
                                />
                              </GridItem>
                              <GridItem colSpan={3}>
                                <ErrorsAndWarningReporter
                                  errors={minimumsCombinationsErrors}
                                  isSSNMapped={isSSNFieldFound}
                                  hideSuccessMessages={true}
                                  fieldErrors={[]}
                                />
                              </GridItem>
                            </Grid>
                          </GridItem>
                        </Grid>
                      )}
                    </Flex>
                  </Box>
                </SimpleGrid>
                {[
                  'nameMinimums',
                  'addressMinimums',
                  'nameCombinations',
                  'addressCombinations',
                ].map((errorType) => {
                  return (
                    <Controller
                      key={errorType}
                      control={control}
                      name={`fieldsInfo.fieldsErrors.${errorType}`}
                      defaultValue={null}
                    />
                  );
                })}
                {showFileProcessorModal && inputFileDetails && (
                  <Modal
                    header='Define File Layout'
                    isOpen={showFileProcessorModal}
                    footer={null}
                    contentProps={{ maxW: '95vw' }}
                    showOk={false}
                    bodyProps={{ pb: 6 }}
                    scrollBehavior='outside'
                    onClose={() => {
                      setInputFileDetails(undefined);
                      setShowFileProcessorModal(false);
                      setIsLoading(false);
                    }}
                    closeOnOverlayClick={false}
                  >
                    {isRemoteFile && (
                      <Modal
                        header='File Header Mapping'
                        isOpen={showMlNotification}
                        contentProps={{ maxW: '31vw' }}
                        onClose={() => {
                          setShowMlNotification(false);
                          setInputFileDetails(undefined);
                          setShowFileProcessorModal(false);
                          setIsLoading(false);
                        }}
                        footer={
                          <>
                            <Button
                              colorScheme='blue'
                              onClick={(_e) => {
                                setShowMlNotification(false);
                              }}
                            >
                              Continue
                            </Button>
                            <Button
                              variant={'outline'}
                              colorScheme='gray'
                              ml={2}
                              onClick={(_e) => {
                                setShowMlNotification(false);
                                setInputFileDetails(undefined);
                                setShowFileProcessorModal(false);
                                setIsLoading(false);
                              }}
                            >
                              Cancel
                            </Button>
                          </>
                        }
                        closeOnOverlayClick={false}
                      >
                        <Text>
                          We automatically mapped standard Experian headers to
                          the ones you provided in your file.
                        </Text>
                        <div>
                          <Text as='b'>
                            Before accepting the layout,{' '}
                            <Text as='i'>
                              please verify that all of your column headers are
                              mapped correctly to the correct Experian headers.
                            </Text>
                          </Text>
                        </div>
                      </Modal>
                    )}
                    <InputFileProcessor
                      productName={productName}
                      setIsRemoteFile={setIsRemoteFile}
                      tmsNumber={data?.orderDetails?.tmsNumber}
                      subCode={data?.subCode}
                      inputLabelsMap={inputLabelsMap}
                      previousFieldMappings={previousFieldMappings}
                      onAfterViewDisplayed={() => {
                        setIsLoading(false);
                      }}
                      details={inputFileDetails}
                      isRemoteFile={isRemoteFile}
                      onNewFileSelected={(
                        fileDetails,
                        currentFieldMappings,
                      ) => {
                        setShowMlNotification(true);
                        setShowFileProcessorModal(false);
                        setInputFileDetails(fileDetails);
                        setValue('delimiter', fileDetails.fieldDelimiter, {
                          shouldDirty: true,
                          shouldValidate: true,
                        });
                        setInputFileInformationModel({
                          ...inputFileInformationModel,
                          delimiter: fileDetails.fieldDelimiter,
                        });
                        setSelectedFieldMappings(currentFieldMappings);
                        console.log('Forcing Re-Render');
                        setShowFileProcessorModal(true);
                        // setFileProcessorComponentKey(generateId());
                      }}
                      onSuccess={(uploadResult) => {
                        // if (onFileSelected) {
                        //   onFileSelected();
                        // }
                        onFileUploadSuccess(uploadResult);
                      }}
                      inputFileSelectionOptions={inputFileSelectionOptions}
                      oneClickRemoteFileAccess={true}
                    />
                  </Modal>
                )}
              </FormLayout>
            </form>
          </Flex>
        </Spin>
      )}
    </>
  );
};
